import { axiosEjFetch } from "./axiosEjHelpers";
import { getInvoices } from "./invoiceService";
import type { invoiceEmailType, reservationInvoiceType } from "utils";

export type RecurringBillingItem = {
  id?: string;
  name: string;
  unitPrice: number;
};

export type RecurringBillingIssuedInvoice = {
  id: string;
  issuedOnUtc: string;
  amount: number;
  currencyCode: string;
  isPaid?: boolean;
  invoiceNumber?: string;
  emails?: invoiceEmailType[];
};

export type RecurringBillingPlan = {
  id?: string;
  reservationId: string;
  startDate: string;
  periodMonths: number;
  serviceFee: number;
  currencyCode: string;
  dueDateInDays: number;
  nextBillingDate?: string | null;
  lastBilledDate?: string | null;
  items: RecurringBillingItem[];
  isActive?: boolean;
};

export type UpsertRecurringBillingRequest = {
  id?: string;
  reservationId: string;
  startDate: string;
  periodMonths: number;
  serviceFee: number;
  currencyCode: string;
  dueDateInDays: number;
  items: Pick<RecurringBillingItem, "name" | "unitPrice">[];
};

type ReservationInvoiceLike = reservationInvoiceType & {
  issuedOnUtc?: string;
  amount?: number;
  isPaid?: boolean;
  paid?: boolean;
};

export function mapToRecurringBillingIssuedInvoice(
  invoice: ReservationInvoiceLike
): RecurringBillingIssuedInvoice {
  return {
    id: invoice.id,
    issuedOnUtc: invoice.issuedOnUtc || invoice.createdOnUtc || invoice.dateOfIssue,
    amount: invoice.amount ?? invoice.grossValue,
    currencyCode: invoice.currencyCode,
    isPaid: invoice.isPaid ?? invoice.paid,
    invoiceNumber: invoice.invoiceNumber,
    emails: invoice.emails ?? [],
  };
}

export async function getRecurringBillingByReservation(
  reservationId: string
): Promise<RecurringBillingPlan | null> {
  return axiosEjFetch.safeGet<undefined, undefined, RecurringBillingPlan | null>(
    `/api/RecurringBilling/getByReservation/${reservationId}`
  );
}

export async function getRecurringBillingInvoicesByReservation(
  reservationId: string
): Promise<RecurringBillingIssuedInvoice[]> {
  const invoices = await getInvoices(reservationId);
  if (!Array.isArray(invoices)) {
    return [];
  }

  return invoices.map((invoice) =>
    mapToRecurringBillingIssuedInvoice(invoice as ReservationInvoiceLike)
  );
}

export async function upsertRecurringBilling(
  body: UpsertRecurringBillingRequest
): Promise<RecurringBillingPlan> {
  const response = await axiosEjFetch.safePost<
    UpsertRecurringBillingRequest,
    undefined,
    undefined,
    RecurringBillingPlan
  >(`/api/RecurringBilling/upsert`, body);

  return response.data;
}

export async function deactivateRecurringBilling(
  reservationId: string
): Promise<void> {
  await axiosEjFetch.post(`/api/RecurringBilling/${reservationId}/deactivate`);
}
